// deps
import React, { useMemo, memo, useCallback, SVGProps, ReactNode } from "react";

// internl-deps
import { format, getAddressInfo, useEffectOnce } from "@swiggy-private/core-ui";
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { default as HomeIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/homeFilled/16";
import { default as HomeNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/homeFilledNotification/16";
import { default as LocationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilled/16";
import { default as LocationNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilledNotification/16";
import { default as WorkIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/workFilled/16";
import { default as WorkNotificationIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/workFilledNotification/16";
import { ADDRESS_CATEGORIES } from "@swiggy-private/core-types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// helpers
import { getHeaderAddressAnnotationText, mapAddressCategoryToAddressType } from "@cart-ui/helpers/CartHeader";

//constants
import { ACCESSIBILITY_TEXTS } from "@cart-ui/constants";

// widgets
import {
    AnnotationContainerStyled,
    AddressIconStyled,
    ContainerStyled,
    SubtitleStyled,
    TitleStyled,
    AddressInfoContainerStyled,
    AddressInfoStyled,
    ChevronRightIconStyled,
    StyledDeliveryAddressIcon,
    ContentStyled,
    TitleTextStyled,
    CartHeaderContentContainer,
    ToingHeaderSeparatorStyled,
} from "./CartHeaderContent.style";
import { UserAddress } from "@swiggy-private/cart-types";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { getSlaText } from "@cart-ui/helpers/DeliverySpeed";
import { isEmpty } from "@swiggy-private/js-utils";
import { hasInvalidAddressCategory } from "@cart-ui/helpers/CartFooter";
import { isToingFlow } from "@swiggy-private/checkout-core";

export interface ToingHeaderContentProps {
    restaurantName: ReactNode;
    handleRestaurantNameClick: VoidFunction;
    selectedAddress?: UserAddress | null;
    onAddressDropdownClick?: VoidFunction;
    onImpressionCartHeaderContent: VoidFunction;
    onClickCartHeaderDropdown: VoidFunction;
    showAddressNudgeIcon?: boolean;
    hasStamp?: boolean;
    isRtbXpEnabled?: boolean;
}

const SLA_TEXT_FORMAT = "{0} to {1}";

const DeliveryAddressIcon = memo(function DeliveryAddressIcon({
    category,
    showAddressNudgeIcon,
    iconColor,
}: {
    category?: number;
    showAddressNudgeIcon?: boolean;
    iconColor: "text_inverse_High_Emphasis" | "text_Highest_Emphasis";
}) {
    let deliveryIcon: (props: SVGProps<SVGSVGElement>) => JSX.Element;

    switch (category) {
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_HOME:
            deliveryIcon = showAddressNudgeIcon ? HomeNotificationIcon : HomeIcon;
            break;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_WORK:
            deliveryIcon = showAddressNudgeIcon ? WorkNotificationIcon : WorkIcon;
            break;
        case ADDRESS_CATEGORIES.ADDRESS_CATEGORY_FRIENDS_AND_FAMILY:
            deliveryIcon = showAddressNudgeIcon ? HomeNotificationIcon : HomeIcon;
            break;
        default:
            deliveryIcon = showAddressNudgeIcon ? LocationNotificationIcon : LocationIcon;
            break;
    }
    return (
        <StyledDeliveryAddressIcon
            data-testid={`${COMMON_HEADER_TEST_IDS.DELIVERY_ADDRESS_ICON}_${mapAddressCategoryToAddressType(category || 0)}`}
            Icon={deliveryIcon}
            fillColor={iconColor}
            strokeColor={iconColor}
        />
    );
});

const ToingHeaderContent = ({
    restaurantName,
    selectedAddress,
    onAddressDropdownClick,
    onImpressionCartHeaderContent,
    onClickCartHeaderDropdown,
    showAddressNudgeIcon,
    hasStamp = false,
    isRtbXpEnabled = false,
}: ToingHeaderContentProps) => {
    const addressInfo = useMemo(() => getAddressInfo(selectedAddress), [selectedAddress]);
    const theme = useTheme();
    const category = useMemo(() => {
        if (hasInvalidAddressCategory(selectedAddress)) {
            return selectedAddress?.addressCategory;
        }
        return selectedAddress?.userIntentAddressCategory;
    }, [selectedAddress]);

    useEffectOnce(() => {
        onImpressionCartHeaderContent();
    });

    const onDropdownClick = useCallback(() => {
        onAddressDropdownClick?.();
        onClickCartHeaderDropdown();
    }, [onAddressDropdownClick, onClickCartHeaderDropdown]);

    const headerTextColor = isRtbXpEnabled ? "text_Highest_Emphasis" : "text_inverse_High_Emphasis";
    const titleTextColor = isRtbXpEnabled ? "text_Low_Emphasis" : "text_inverse_Med_Emphasis";

    const slaText = useMemo(() => {
        const sla = selectedAddress?.sla_options?.find((sla) => sla.type === selectedAddress?.selected_sla_option);

        const slaText = getSlaText(sla);

        let addressAnnotation: string;
        if (hasInvalidAddressCategory(selectedAddress)) {
            addressAnnotation = getHeaderAddressAnnotationText(selectedAddress, category);
        } else {
            addressAnnotation =
                selectedAddress?.annotation || getHeaderAddressAnnotationText(selectedAddress, category);
        }

        if (isEmpty(slaText)) {
            return addressAnnotation;
        }
        return format(SLA_TEXT_FORMAT, slaText, addressAnnotation);
    }, [selectedAddress, category]);

    const renderAddressAndSla = useMemo(() => {
        return (
            <>
                <AnnotationContainerStyled>
                    <DeliveryAddressIcon
                        category={category}
                        showAddressNudgeIcon={showAddressNudgeIcon}
                        iconColor={headerTextColor}
                    />
                    <Text
                        data-testid={COMMON_HEADER_TEST_IDS.ADDRESS_ANNOTATION_TEXT}
                        color={headerTextColor}
                        variant="V2.body3.neutral.medium"
                    >
                        {slaText}
                    </Text>
                </AnnotationContainerStyled>
                <AddressInfoContainerStyled>
                    <ToingHeaderSeparatorStyled $isRtbXpEnabled={isRtbXpEnabled}>|</ToingHeaderSeparatorStyled>
                    <AddressInfoStyled variant="V2.body3.neutral.medium" color={headerTextColor}>
                        {addressInfo}
                    </AddressInfoStyled>
                </AddressInfoContainerStyled>
            </>
        );
    }, [showAddressNudgeIcon, addressInfo, slaText, category, headerTextColor, isRtbXpEnabled]);

    const renderTitle = useMemo(() => {
        return (
            <TitleTextStyled color={titleTextColor} variant="V2.body3.neutral.medium">
                {restaurantName}
            </TitleTextStyled>
        );
    }, [restaurantName, titleTextColor]);

    const renderSubtitle = useMemo(() => {
        if (selectedAddress) {
            return renderAddressAndSla;
        }
    }, [renderAddressAndSla, selectedAddress]);

    return (
        <ContainerStyled>
            <CartHeaderContentContainer isToing={isToingFlow()} hasStamp={hasStamp}>
                <ContentStyled $isRtbXpEnabled={isRtbXpEnabled}>
                    <TitleStyled data-testid={COMMON_HEADER_TEST_IDS.RESTAURANT_NAME}>{renderTitle}</TitleStyled>
                    <SubtitleStyled data-testid={COMMON_HEADER_TEST_IDS.DELIVERY_ADDRESS} onClick={onDropdownClick}>
                        {renderSubtitle}
                    </SubtitleStyled>
                </ContentStyled>

                {selectedAddress && (
                    <AddressIconStyled
                        data-testid={COMMON_HEADER_TEST_IDS.RIGHT_ICON}
                        aria-label={ACCESSIBILITY_TEXTS.CHANGE_ADDRESS}
                        onClick={onDropdownClick}
                    >
                        <ChevronRightIconStyled
                            strokeOpacity={0.6}
                            strokeWidth={2}
                            strokeColor={
                                isRtbXpEnabled
                                    ? theme.colors[theme.colorMode].text_Highest_Emphasis
                                    : theme.colors[theme.colorMode].text_inverse_High_Emphasis
                            }
                        />
                    </AddressIconStyled>
                )}
            </CartHeaderContentContainer>
        </ContainerStyled>
    );
};

export default memo(ToingHeaderContent);
